import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { SortingState } from '@tanstack/react-table';
import { useStudies } from '../../hooks/useStudies';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import type { StudyQuery } from '../../data/DataSource';
import { WorklistFilters } from './WorklistFilters';
import { StudyTable } from './StudyTable';

export function WorklistPage() {
  const [params, setParams] = useSearchParams();

  const patient = params.get('q') ?? '';
  const modality = params.get('modality') ?? '';
  const collection = params.get('collection') ?? '';
  const tag = params.get('tag') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const sortParam = params.get('sort') ?? '';

  // The header's workspace decides WHICH studies exist for this list, so it is
  // part of the query and part of its cache key — switching workspace refetches
  // rather than re-filtering a cached set that was fetched for another one.
  const { current, ready } = useSelectedWorkspace();
  const query = useMemo<StudyQuery>(
    () => ({
      patientName: patient || undefined,
      modality: modality || undefined,
      // Filters, unlike `workspace`: they narrow which of this caller's studies
      // are listed rather than whose studies they may see. A collection also
      // switches the server's ORDER to the user's own sequence.
      collection: collection || undefined,
      tags: tag ? [tag] : undefined,
      workspace: current?.public_id,
    }),
    [patient, modality, collection, tag, current?.public_id],
  );
  const { data: studies, isLoading, error } = useStudies(query, ready);
  // A disabled query is not "loading" as far as react-query is concerned — it is
  // idle — so without this the list renders its empty state for the tick before
  // the workspace resolves, and "no studies" is a worse lie than a spinner.
  const busy = isLoading || !ready;

  // ?sort=column:dir  <->  TanStack SortingState
  const sorting: SortingState = useMemo(() => {
    if (!sortParam) return [];
    const [id, dir] = sortParam.split(':');
    return id ? [{ id, desc: dir === 'desc' }] : [];
  }, [sortParam]);

  /** Merge updates into the URL; clear keys set to empty/undefined. */
  const patch = (updates: Record<string, string | number | undefined>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v === undefined || v === '' || v === 0) next.delete(k);
          else next.set(k, String(v));
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <WorklistFilters
        initialPatient={patient}
        initialModality={modality}
        initialCollection={collection}
        initialTag={tag}
        onChange={(q) =>
          patch({
            q: q.patientName,
            modality: q.modality,
            collection: q.collection,
            tag: q.tags?.[0],
            page: undefined,
          })
        }
      />

      {busy && (
        <div className="flex min-h-50 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <div className="mb-1 font-semibold">Failed to load studies</div>
          {(error as Error).message}
        </div>
      )}

      {studies && studies.length === 0 && (
        <div className="flex min-h-50 items-center justify-center text-sm text-muted-foreground">
          No studies found.
        </div>
      )}

      {studies && studies.length > 0 && (
        <div className="min-h-0 flex-1">
          <StudyTable
            studies={studies}
            sorting={sorting}
            onSortingChange={(updater) => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              const s = next[0];
              patch({ sort: s ? `${s.id}:${s.desc ? 'desc' : 'asc'}` : undefined, page: undefined });
            }}
            pageIndex={page - 1}
            onPageIndexChange={(i) => patch({ page: i + 1 })}
          />
        </div>
      )}
    </div>
  );
}
